import React from 'react';
import classNames from 'classnames/bind';
import {
  Container,
  Segment,
  Button,
  Dropdown,
  Loader,
  Popup,
  Form,
  Input,
  Modal,
  Grid,
  Icon,
  Checkbox
} from 'semantic-ui-react';

import { connect } from 'react-redux';
import {
  fetchTeam,
  fetchUsers,
  fetchStatus,
  updateStatus,
  createSnapshot,
  clearTeam
} from '../actions/teams';

import { deleteSlackStatusIntegration } from '../actions/integrations';

import TeamUsersGrid from '../components/TeamUsersGrid';
import TeamLocation from '../utils/TeamLocation';
import StatusLock from '../components/StatusLock';

import '../styles/Team.css';

const UNKNOWN_STATUS = 'unknown';
const JOIN_STATUS = 'join';
const LEAVE_STATUS = 'leave';

const DEFAULT_VIEW_MODE = 'default';
const STATUS_VIEW_MODE = 'status';
const LOCATION_VIEW_MODE = 'location';
const TIMEZONE_VIEW_MODE = 'timeZone';

const SLACK_STATUS_INTEGRATION = 'slack_status';

class Team extends React.Component {
  constructor(props) {
    super(props);

    this.handleStatusChange = this.handleStatusChange.bind(this);
    this.handleStatusWfhClick = this.handleStatusWfhClick.bind(this);
    this.handleLocationChange = this.handleLocationChange.bind(this);
    this.handleReasonSelect = this.handleReasonSelect.bind(this);
    this.handleReasonChange = this.handleReasonChange.bind(this);
    this.handleReasonClear = this.handleReasonClear.bind(this);
    this.handleReasonModalKeep = this.handleReasonModalKeep.bind(this);
    this.handleReasonModalClear = this.handleReasonModalClear.bind(this);
    this.handleSnapshot = this.handleSnapshot.bind(this);
    this.handleViewModeChange = this.handleViewModeChange.bind(this);
    this.handleSyncWithSlackChange = this.handleSyncWithSlackChange.bind(this);

    this.state = {
      wfh: false,
      location: undefined,
      reason: undefined,
      timeZone: undefined,
      viewMode: DEFAULT_VIEW_MODE,
      reasonModalOpen: false,
      reasonPopupOpen: false,
      updatesPopupOpen: false
    };
  }

  componentWillMount() {
    this.createWsSubscription();
    this.fetchData(this.teamId);
    this.setState({
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  }

  componentWillReceiveProps(nextProps) {
    if (this.teamId !== nextProps.match.params.teamId) {
      this.fetchData(nextProps.match.params.teamId);
    }

    this.setState({
      wfh: (nextProps.status && nextProps.status.wfh) || false,
      location:
        nextProps.status &&
        nextProps.status.location &&
        nextProps.status.location.id
    });
  }

  componentWillUnmount() {
    this.props.clearTeam();
    this.closeWsSubscription();
  }

  get teamId() {
    return this.props.match.params.teamId;
  }

  get action() {
    return this.props.status && this.props.status.action;
  }

  get reason() {
    return this.props.status && this.props.status.reason;
  }

  get slackIntegration() {
    return (
      this.props.team &&
      this.props.team.integrations.find(
        integration => integration.name === SLACK_STATUS_INTEGRATION
      )
    );
  }

  createWsSubscription() {
    const ws = new WebSocket(process.env.REACT_APP_WS_URL);
    const subscribeCommand = JSON.stringify({
      command: 'subscribe',
      identifier: JSON.stringify({
        channel: 'TeamChannel',
        team_id: this.teamId
      })
    });

    ws.onopen = () => {
      ws.send(subscribeCommand);
      this.teamChannel = ws;
    };

    ws.onmessage = this.teamChannelOnMessage.bind(this);
  }

  closeWsSubscription() {
    return this.teamChannel && this.teamChannel.close();
  }

  teamChannelOnMessage(message) {
    const data = JSON.parse(message.data);
    const payload = data.message;

    if (data.type !== undefined) {
      return true;
    }

    if (payload === undefined) {
      return true;
    }

    if (
      this.props.currentUser &&
      this.props.status &&
      payload.meta &&
      payload.meta.user_id === this.props.currentUser.id &&
      payload.meta.status_id !== this.props.status.id
    ) {
      this.props.fetchStatus(this.teamId);
    }

    this.props.fetchUsers(this.teamId);

    return true;
  }

  fetchData(teamId) {
    this.props.fetchTeam(teamId);
    this.props.fetchStatus(teamId);
    this.props.fetchUsers(teamId);
  }

  buildStatusData(startingData) {
    const statusLock = new StatusLock().isEnabled;

    return { ...startingData, lock: statusLock };
  }

  handleStatusWfhClick(event) {
    event.preventDefault();

    const { timeZone } = this.state;
    const action = this.props.status
      ? this.props.status.action
      : UNKNOWN_STATUS;

    const updateStatusData = this.buildStatusData({
      action,
      time_zone: timeZone,
      wfh: !this.state.wfh
    });

    this.props.updateStatus(this.teamId, updateStatusData);
  }

  handleLocationChange(event, data) {
    const { timeZone } = this.state;
    const { action, reason } = this.props.status;

    const updateStatusData = this.buildStatusData({
      action,
      reason,
      wfh: false,
      time_zone: timeZone,
      location: { id: data.value }
    });

    this.props.updateStatus(this.teamId, updateStatusData);
  }

  handleStatusChange(event, data) {
    const { timeZone } = this.state;
    const wfh = this.props.status ? this.props.status.wfh : false;

    const updateStatusData = this.buildStatusData({
      action: data.action === undefined ? this.action : data.action,
      reason: data.reason === undefined ? this.reason : data.reason,
      time_zone: timeZone,
      wfh
    });

    if (
      updateStatusData.reason &&
      [JOIN_STATUS, LEAVE_STATUS].includes(data.action)
    ) {
      this.updateStatusData = updateStatusData;
      this.openReasonModal();
    } else {
      this.props.updateStatus(this.teamId, updateStatusData);
    }
  }

  handleReasonSelect(reason, action) {
    this.handleStatusChange({}, { reason, action });
  }

  handleReasonChange(event, data) {
    this.setState({ reason: data.value });
  }

  handleReasonClear() {
    if (this.reason === '') {
      return true;
    }

    return this.handleStatusChange({}, { reason: '' });
  }

  handleReasonModalKeep() {
    this.props.updateStatus(this.teamId, this.updateStatusData);
    this.closeReasonModal();
  }

  handleReasonModalClear() {
    this.props.updateStatus(this.teamId, {
      ...this.updateStatusData,
      reason: ''
    });
    this.closeReasonModal();
  }

  handleSnapshot(snapshot) {
    const createSnapshotData = {
      source: 'camera',
      image_data: snapshot
    };

    this.props.createSnapshot(this.teamId, createSnapshotData);
  }

  handleViewModeChange(event, { value }) {
    this.setState({ viewMode: value });
  }

  handleSyncWithSlackChange(event, data) {
    if (data.checked) {
      window.location.href = `${
        process.env.REACT_APP_AUTH_SLACK_STATUS_URL
      }?user=${this.props.currentUser.id}&team=${this.teamId}`;
    } else {
      this.props.deleteSlackStatusIntegration(this.teamId);
    }
  }

  findLocation(locationId) {
    return (
      this.props.team && new TeamLocation(this.props.team.locations, locationId)
    );
  }

  openReasonModal() {
    this.setState({ reasonModalOpen: true });
  }

  closeReasonModal() {
    this.setState({ reasonModalOpen: false });
  }

  openReasonPopup() {
    this.setState({ reasonPopupOpen: true });
  }

  closeReasonPopup() {
    this.setState({ reasonPopupOpen: false });
  }

  openUpdatesPopup() {
    this.setState({ updatesPopupOpen: true });
  }

  closeUpdatesPopup() {
    this.setState({ updatesPopupOpen: false });
  }

  renderReasonModal() {
    return (
      <Modal
        size="mini"
        open={this.state.reasonModalOpen}
        onClose={() => this.closeReasonModal()}
      >
        <Modal.Content>
          <h3>Keep your status message?</h3>
          <q>{this.reason}</q>
        </Modal.Content>
        <Modal.Actions>
          <Button
            positive
            content="Keep"
            onClick={this.handleReasonModalKeep}
          />
          <Button content="Clear" onClick={this.handleReasonModalClear} />
        </Modal.Actions>
      </Modal>
    );
  }

  renderReasonButton() {
    return (
      <Popup
        inverted
        on="click"
        open={this.state.reasonPopupOpen}
        wide="very"
        position="bottom center"
        onOpen={() => this.openReasonPopup()}
        onClose={() => this.closeReasonPopup()}
        trigger={<Button primary={!!this.reason} icon="talk" />}
      >
        <Form
          inverted
          onSubmit={() => {
            this.closeReasonPopup();
            this.handleReasonSelect(this.state.reason);
          }}
        >
          <Segment basic compact>
            <Input
              placeholder="Wanna say something?"
              defaultValue={this.reason}
              onChange={this.handleReasonChange}
              ref={input => input && input.focus()}
              className="status-change-action__input"
            />
            <Icon
              link
              disabled={!this.reason}
              name="cancel"
              onClick={() => {
                this.closeReasonPopup();
                this.handleReasonClear();
              }}
            />
          </Segment>
        </Form>
      </Popup>
    );
  }

  renderSettingButton() {
    const viewModeOptions = [
      {
        text: 'Grid',
        value: DEFAULT_VIEW_MODE
      },
      {
        text: 'Group by status',
        value: STATUS_VIEW_MODE
      },
      {
        text: 'Group by location',
        value: LOCATION_VIEW_MODE
      },
      {
        text: 'Group by time zone',
        value: TIMEZONE_VIEW_MODE
      }
    ];

    const viewModeLabels = {
      [DEFAULT_VIEW_MODE]: 'Grid',
      [STATUS_VIEW_MODE]: 'Status',
      [LOCATION_VIEW_MODE]: 'Location',
      [TIMEZONE_VIEW_MODE]: 'TZ'
    };

    const viewModeIcons = {
      [DEFAULT_VIEW_MODE]: 'grid layout',
      [STATUS_VIEW_MODE]: 'tag',
      [LOCATION_VIEW_MODE]: 'map marker alternate',
      [TIMEZONE_VIEW_MODE]: 'clock'
    };

    return (
      <Dropdown
        button
        basic
        labeled
        floating
        icon={viewModeIcons[this.state.viewMode]}
        text={viewModeLabels[this.state.viewMode]}
        pointing="top right"
        className="icon"
      >
        <Dropdown.Menu>
          {viewModeOptions.map(viewModeOption => (
            <Dropdown.Item
              key={`view-mode-${viewModeOption.value}`}
              text={viewModeOption.text}
              value={viewModeOption.value}
              onClick={this.handleViewModeChange}
              active={this.state.viewMode === viewModeOption.value}
            />
          ))}
          <Dropdown.Divider />
          <Dropdown.Item
            text="Activity log"
            onClick={() =>
              this.props.history.push(`/team/${this.teamId}/activity`)
            }
          />
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  renderUpdatesControl() {
    return (
      <Popup
        on="click"
        open={this.state.updatesPopupOpen}
        wide="very"
        position="bottom center"
        onOpen={() => this.openUpdatesPopup()}
        onClose={() => this.closeUpdatesPopup()}
        trigger={<Button basic icon="check" content="Updates" />}
      >
        <React.Fragment>
          <Segment basic compact>
            <Checkbox
              toggle
              label="Slack sync"
              checked={this.slackIntegration && this.slackIntegration.enabled}
              onChange={this.handleSyncWithSlackChange}
            />
          </Segment>
          <Segment basic compact>
            <StatusLock />
          </Segment>
        </React.Fragment>
      </Popup>
    );
  }

  renderPrimaryToolbar() {
    const statusChangeActions = {
      join: 'ON',
      afk: 'AFK',
      leave: 'OFF'
    };

    const statusChangeActionColors = {
      join: this.action === 'join' && 'green',
      afk: this.action === 'afk' && 'yellow',
      leave: this.action === 'leave' && 'red'
    };

    const statusChangeActionIcons = {
      join: 'play',
      afk: 'pause',
      leave: 'stop'
    };

    const currentLocation =
      this.state.location && this.findLocation(this.state.location);

    const locationClassNames = {
      primary: this.state.location
    };

    const predefinedReasonsByAction = {
      afk: {
        Commute: 'bus',
        Lunch: 'food',
        'Short break': 'clock'
      }
    };

    const { team } = this.props;

    return (
      <Segment basic className="Team__primary-toolbar">
        <Grid>
          <Grid.Column width={10}>
            <Button.Group className="Team__primary-toolbar__group">
              <Dropdown
                floating
                labeled
                button
                icon="building outline"
                className={classNames('icon', locationClassNames)}
                pointing="top left"
                loading={!this.props.status}
                text={
                  currentLocation ? currentLocation.name : 'Select location'
                }
              >
                <Dropdown.Menu>
                  {team &&
                    team.locations.map(location => (
                      <Dropdown.Item
                        key={`${location.id}-location`}
                        value={location.id}
                        onClick={this.handleLocationChange}
                        active={location.id === this.state.location}
                      >
                        {location.name}
                      </Dropdown.Item>
                    ))}
                </Dropdown.Menu>
              </Dropdown>
              <Button
                primary={this.state.wfh}
                onClick={this.handleStatusWfhClick}
                active={this.state.wfh}
                icon="home"
                content="WFH"
              />
            </Button.Group>{' '}
            <Button.Group className="Team__primary-toolbar__group">
              {Object.keys(statusChangeActions).map(action =>
                predefinedReasonsByAction[action] ? (
                  <Popup
                    hoverable
                    key={`${action}-status-change`}
                    wide="very"
                    on="hover"
                    position="bottom center"
                    trigger={
                      <Button
                        color={statusChangeActionColors[action] || undefined}
                        icon={statusChangeActionIcons[action] || undefined}
                        onClick={this.handleStatusChange}
                        disabled={!this.action || this.action === action}
                        action={action}
                        content={statusChangeActions[action]}
                      />
                    }
                  >
                    <Button.Group>
                      {Object.entries(predefinedReasonsByAction[action]).map(
                        ([reason, icon]) => (
                          <Button
                            basic
                            compact
                            key={`${reason}-reason`}
                            onClick={() =>
                              this.handleReasonSelect(reason, action)
                            }
                            content={reason}
                            icon={icon}
                          />
                        )
                      )}
                    </Button.Group>
                  </Popup>
                ) : (
                  <Button
                    key={`${action}-status-change`}
                    color={statusChangeActionColors[action] || undefined}
                    icon={statusChangeActionIcons[action] || undefined}
                    onClick={this.handleStatusChange}
                    disabled={!this.action || this.action === action}
                    action={action}
                    content={statusChangeActions[action]}
                  />
                )
              )}
            </Button.Group>{' '}
            <Button.Group className="Team__primary-toolbar__group">
              {this.renderReasonButton()}
            </Button.Group>{' '}
            <Button.Group className="Team__primary-toolbar__group">
              {this.renderUpdatesControl()}
            </Button.Group>
          </Grid.Column>
          <Grid.Column width={2} textAlign="right">
            {this.renderSettingButton()}
          </Grid.Column>
        </Grid>
      </Segment>
    );
  }

  render() {
    const { team, users, errored, currentUser } = this.props;
    const { viewMode } = this.state;

    return (
      <Container fluid className="Team">
        {this.renderReasonModal()}
        <Loader active={!team && !errored} />
        {team && (
          <div>
            <Container fluid>{this.renderPrimaryToolbar()}</Container>
            <Container fluid>
              {users && (
                <TeamUsersGrid
                  users={users}
                  currentUser={currentUser}
                  locations={team.locations}
                  viewMode={viewMode}
                  onSnapshot={this.handleSnapshot}
                />
              )}
            </Container>
          </div>
        )}
      </Container>
    );
  }
}

const mapStateToProps = state => ({
  team: state.teams.team,
  users: state.teams.users,
  status: state.teams.status,
  errored: state.teams.error
});

const mapDispatchToProps = dispatch => ({
  fetchTeam: teamId => {
    dispatch(fetchTeam(teamId));
  },
  clearTeam: () => {
    dispatch(clearTeam());
  },
  fetchUsers: teamId => {
    dispatch(fetchUsers(teamId));
  },
  fetchStatus: teamId => {
    dispatch(fetchStatus(teamId));
  },
  updateStatus: (teamId, params) => {
    dispatch(updateStatus(teamId, params)).then(() =>
      dispatch(fetchUsers(teamId))
    );
  },
  createSnapshot: (teamId, params) => {
    dispatch(createSnapshot(teamId, params)).then(() =>
      dispatch(fetchUsers(teamId))
    );
  },
  deleteSlackStatusIntegration: teamId => {
    dispatch(deleteSlackStatusIntegration(teamId)).then(() =>
      dispatch(fetchTeam(teamId))
    );
  }
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Team);



// WEBPACK FOOTER //
// ./src/containers/Team.js